import {
  SimpleGrid,
  Paper,
  Title,
  Text,
  Group,
  Stack,
  Table,
  Skeleton,
  Alert,
  Badge,
  Button,
  Timeline,
} from '@mantine/core';
import { LineChart } from '@mantine/charts';
import { useNavigate } from 'react-router-dom';
import {
  IconCurrencyDollar,
  IconShoppingCart,
  IconPackage,
  IconPercentage,
  IconAlertTriangle,
  IconTruck,
  IconFilePlus,
  IconFileInvoice,
  IconCash,
  IconBuildingBank,
  IconActivity,
  IconBoxSeam,
  IconReceipt,
  IconUser,
} from '@tabler/icons-react';
import StatCard from '@/components/StatCard';
import StatusBadge from '@/components/StatusBadge';
import PageHeader from '@/components/PageHeader';
import AIInsights from '@/components/AIInsights';
import { useDashboardKPIs } from '@/hooks/useApi';
import { useHomeDashboard } from '@/hooks/api/useDashboard';
import { useAuthStore } from '@/store/auth';
import type { DashboardKPIs } from '@/hooks/api/useDashboard';

// ---------------------------------------------------------------------------
// Formatters
// ---------------------------------------------------------------------------

function formatCurrency(val: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(val);
}

function formatNumber(val: number): string {
  return new Intl.NumberFormat('en-US').format(val);
}

const clickableRowStyle = {
  cursor: 'pointer' as const,
  transition: 'background-color 100ms ease',
};

// ---------------------------------------------------------------------------
// Quick Actions
// ---------------------------------------------------------------------------

function QuickActions() {
  const navigate = useNavigate();

  const actions = [
    { label: 'New Order', icon: IconFilePlus, color: 'blue', path: '/sales/new-order' },
    { label: 'New Invoice', icon: IconFileInvoice, color: 'teal', path: '/sales/orders' },
    { label: 'Receive Payment', icon: IconCash, color: 'green', path: '/sales/cash-receipts' },
    { label: 'Bank Deposit', icon: IconBuildingBank, color: 'violet', path: '/accounting/banking' },
  ];

  return (
    <Paper p="md" withBorder>
      <Title order={4} mb="md">
        Quick Actions
      </Title>
      <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="sm">
        {actions.map((action) => {
          const Icon = action.icon;
          return (
            <Button
              key={action.label}
              variant="light"
              color={action.color}
              leftSection={<Icon size={18} />}
              onClick={() => navigate(action.path)}
              fullWidth
              size="md"
              styles={{
                root: { height: 'auto', paddingBlock: 12 },
                inner: { justifyContent: 'flex-start' },
              }}
            >
              {action.label}
            </Button>
          );
        })}
      </SimpleGrid>
    </Paper>
  );
}

// ---------------------------------------------------------------------------
// Today's Stats (from Home Dashboard KPIs)
// ---------------------------------------------------------------------------

function TodayStats() {
  const { data: home, isLoading } = useHomeDashboard(1);

  if (isLoading || !home) {
    return (
      <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="md">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} height={100} radius="md" />
        ))}
      </SimpleGrid>
    );
  }

  return (
    <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="md">
      <StatCard
        icon={<IconShoppingCart size={22} />}
        label="Orders Today"
        value={formatNumber(home.revenue?.order_count ?? 0)}
        color="blue"
      />
      <StatCard
        icon={<IconBoxSeam size={22} />}
        label="Shipped Today"
        value={formatNumber(home.operations?.orders_shipped ?? 0)}
        color="teal"
      />
      <StatCard
        icon={<IconCurrencyDollar size={22} />}
        label="Revenue Today"
        value={formatCurrency(home.revenue?.total_revenue ?? 0)}
        trend={home.revenue?.revenue_trend}
        trendLabel="vs avg"
        color="green"
      />
      <StatCard
        icon={<IconReceipt size={22} />}
        label="Cash Collected"
        value={formatCurrency(home.ar?.total_outstanding ?? 0)}
        color="orange"
      />
    </SimpleGrid>
  );
}

// ---------------------------------------------------------------------------
// Recent Activity Feed
// ---------------------------------------------------------------------------

interface ActivityItem {
  key: string;
  icon: React.ReactNode;
  color: string;
  title: string;
  detail: string;
  time: string;
}

function RecentActivity({ kpis }: { kpis: DashboardKPIs }) {
  // Build activity items from the most recent orders and deliveries
  const items: ActivityItem[] = [];

  for (const o of kpis.recent_orders.slice(0, 3)) {
    items.push({
      key: `order-${o.order_number}`,
      icon: <IconShoppingCart size={16} />,
      color: 'blue',
      title: `Order ${o.order_number}`,
      detail: `${o.customer_name} -- ${formatCurrency(o.total)}`,
      time: o.date,
    });
  }

  for (const d of kpis.upcoming_deliveries.slice(0, 2)) {
    items.push({
      key: `delivery-${d.po_number}`,
      icon: <IconTruck size={16} />,
      color: 'teal',
      title: `Delivery ${d.po_number}`,
      detail: `${d.vendor_name} -- ${d.item_count} items`,
      time: d.expected_delivery,
    });
  }

  for (const a of kpis.compliance_alerts.slice(0, 2)) {
    items.push({
      key: `alert-${a.type}-${a.date}`,
      icon: <IconAlertTriangle size={16} />,
      color: a.severity === 'critical' ? 'red' : a.severity === 'high' ? 'orange' : 'yellow',
      title: a.type,
      detail: a.message,
      time: a.date,
    });
  }

  if (items.length === 0) {
    return (
      <Paper p="md" withBorder>
        <Group gap="sm" mb="md">
          <IconActivity size={20} opacity={0.5} />
          <Title order={4}>Recent Activity</Title>
        </Group>
        <Text size="sm" c="dimmed" ta="center" py="lg">
          No recent activity to display
        </Text>
      </Paper>
    );
  }

  return (
    <Paper p="md" withBorder>
      <Group gap="sm" mb="md">
        <IconActivity size={20} opacity={0.5} />
        <Title order={4}>Recent Activity</Title>
      </Group>
      <Timeline active={items.length - 1} bulletSize={28} lineWidth={2}>
        {items.map((item) => (
          <Timeline.Item
            key={item.key}
            bullet={item.icon}
            color={item.color}
            title={
              <Text size="sm" fw={500}>
                {item.title}
              </Text>
            }
          >
            <Text size="sm" c="dimmed" lineClamp={1}>
              {item.detail}
            </Text>
            <Text size="xs" c="dimmed" mt={2}>
              {item.time}
            </Text>
          </Timeline.Item>
        ))}
      </Timeline>
    </Paper>
  );
}

// ---------------------------------------------------------------------------
// Main Dashboard
// ---------------------------------------------------------------------------

export default function Dashboard() {
  const { data: kpis, isLoading, error } = useDashboardKPIs();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);

  // Default empty arrays/zeros for missing fields when API returns partial data
  const safeKpis: DashboardKPIs = {
    revenue_30d: 0, revenue_trend: 0, orders_30d: 0, orders_trend: 0,
    inventory_value: 0, inventory_trend: 0, gross_margin_pct: 0, margin_trend: 0,
    sales_trend: [], top_products: [], low_stock_alerts: [],
    recent_orders: [], upcoming_deliveries: [], compliance_alerts: [],
    ...kpis,
  };

  if (isLoading || !kpis) {
    return (
      <Stack gap="md">
        <Title order={2}>Dashboard</Title>
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="md">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} height={120} radius="md" />
          ))}
        </SimpleGrid>
        <Skeleton height={200} radius="md" />
        <Skeleton height={300} radius="md" />
        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
          <Skeleton height={300} radius="md" />
          <Skeleton height={300} radius="md" />
        </SimpleGrid>
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      {/* ---- Welcome + Page Header ---- */}
      <PageHeader
        title={user ? `Welcome back, ${user.display_name || user.username}` : 'Dashboard'}
        description="Overview of key business metrics and AI-powered insights"
      />

      {/* ---- Today's Stats ---- */}
      <TodayStats />

      {/* ---- Quick Actions ---- */}
      <QuickActions />

      {/* ---- AI Insights ---- */}
      <AIInsights />

      {/* ---- KPI Cards (30-day) ---- */}
      <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="md">
        <StatCard
          icon={<IconCurrencyDollar size={22} />}
          label="Revenue (30d)"
          value={formatCurrency(safeKpis.revenue_30d)}
          trend={safeKpis.revenue_trend}
          trendLabel="vs prior period"
          onClick={() => navigate('/sales/orders')}
        />
        <StatCard
          icon={<IconShoppingCart size={22} />}
          label="Orders (30d)"
          value={formatNumber(safeKpis.orders_30d)}
          trend={safeKpis.orders_trend}
          trendLabel="vs prior period"
          color="blue"
          onClick={() => navigate('/sales/orders')}
        />
        <StatCard
          icon={<IconPackage size={22} />}
          label="Inventory Value"
          value={formatCurrency(safeKpis.inventory_value)}
          trend={safeKpis.inventory_trend}
          trendLabel="vs prior period"
          color="violet"
          onClick={() => navigate('/inventory/products')}
        />
        <StatCard
          icon={<IconPercentage size={22} />}
          label="Gross Margin"
          value={`${(safeKpis.gross_margin_pct ?? 0).toFixed(1)}%`}
          trend={safeKpis.margin_trend}
          trendLabel="vs prior period"
          color="orange"
        />
      </SimpleGrid>

      {/* ---- Sales Trend Chart ---- */}
      <Paper p="md">
        <Title order={4} mb="md">
          Sales Trend (Last 30 Days)
        </Title>
        <LineChart
          h={300}
          data={safeKpis.sales_trend}
          dataKey="date"
          series={[
            { name: 'revenue', color: 'brand.6', label: 'Revenue' },
          ]}
          curveType="monotone"
          withLegend
          withTooltip
          tooltipAnimationDuration={200}
          valueFormatter={(val) => formatCurrency(val)}
        />
      </Paper>

      <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
        {/* ---- Top Products ---- */}
        <Paper p="md">
          <Group justify="space-between" mb="md">
            <Title order={4}>Top Products by Revenue</Title>
            <Button variant="subtle" size="xs" onClick={() => navigate('/inventory/products')}>
              View All
            </Button>
          </Group>
          <Table striped verticalSpacing="xs" horizontalSpacing="sm">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Product</Table.Th>
                <Table.Th>SKU</Table.Th>
                <Table.Th ta="right">Revenue</Table.Th>
                <Table.Th ta="right">Qty</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {safeKpis.top_products.map((p) => (
                <Table.Tr
                  key={p.sku}
                  style={clickableRowStyle}
                  className="hoverable-row"
                  onClick={() => navigate('/inventory/products')}
                >
                  <Table.Td>
                    <Text size="sm" lineClamp={1}>
                      {p.product_name}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed">
                      {p.sku}
                    </Text>
                  </Table.Td>
                  <Table.Td ta="right">
                    <Text size="sm" fw={500}>
                      {formatCurrency(p.revenue)}
                    </Text>
                  </Table.Td>
                  <Table.Td ta="right">
                    <Text size="sm">{formatNumber(p.quantity)}</Text>
                  </Table.Td>
                </Table.Tr>
              ))}
              {safeKpis.top_products.length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={4}>
                    <Text size="sm" c="dimmed" ta="center">
                      No data available
                    </Text>
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        </Paper>

        {/* ---- Low Stock Alerts ---- */}
        <Paper p="md">
          <Group justify="space-between" mb="md">
            <Group gap="sm">
              <Title order={4}>Low Stock Alerts</Title>
              <Badge color="red" variant="light">
                {safeKpis.low_stock_alerts.length}
              </Badge>
            </Group>
            <Button variant="subtle" size="xs" onClick={() => navigate('/inventory/products')}>
              View All
            </Button>
          </Group>
          <Table striped verticalSpacing="xs" horizontalSpacing="sm">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Product</Table.Th>
                <Table.Th ta="right">On Hand</Table.Th>
                <Table.Th ta="right">Reorder Pt</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {safeKpis.low_stock_alerts.map((a) => (
                <Table.Tr
                  key={a.sku}
                  style={clickableRowStyle}
                  className="hoverable-row"
                  onClick={() => navigate('/inventory/products')}
                >
                  <Table.Td>
                    <Stack gap={0}>
                      <Text size="sm" lineClamp={1}>
                        {a.product_name}
                      </Text>
                      <Text size="xs" c="dimmed">
                        {a.sku}
                      </Text>
                    </Stack>
                  </Table.Td>
                  <Table.Td ta="right">
                    <Text size="sm" c="red" fw={600}>
                      {a.on_hand}
                    </Text>
                  </Table.Td>
                  <Table.Td ta="right">
                    <Text size="sm">{a.reorder_point}</Text>
                  </Table.Td>
                </Table.Tr>
              ))}
              {safeKpis.low_stock_alerts.length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={3}>
                    <Text size="sm" c="dimmed" ta="center">
                      All stock levels healthy
                    </Text>
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        </Paper>
      </SimpleGrid>

      <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
        {/* ---- Recent Orders ---- */}
        <Paper p="md">
          <Group justify="space-between" mb="md">
            <Title order={4}>Recent Orders</Title>
            <Button variant="subtle" size="xs" onClick={() => navigate('/sales/orders')}>
              View All
            </Button>
          </Group>
          <Table striped verticalSpacing="xs" horizontalSpacing="sm">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Order</Table.Th>
                <Table.Th>Customer</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th ta="right">Total</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {safeKpis.recent_orders.map((o) => (
                <Table.Tr
                  key={o.order_number}
                  style={clickableRowStyle}
                  className="hoverable-row"
                  onClick={() => navigate('/sales/orders')}
                >
                  <Table.Td>
                    <Text size="sm" fw={500}>
                      {o.order_number}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" lineClamp={1}>
                      {o.customer_name}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <StatusBadge status={o.status} size="xs" />
                  </Table.Td>
                  <Table.Td ta="right">
                    <Text size="sm">{formatCurrency(o.total)}</Text>
                  </Table.Td>
                </Table.Tr>
              ))}
              {safeKpis.recent_orders.length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={4}>
                    <Text size="sm" c="dimmed" ta="center">
                      No recent orders
                    </Text>
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        </Paper>

        {/* ---- Recent Activity Feed ---- */}
        <RecentActivity kpis={safeKpis} />
      </SimpleGrid>

      {/* ---- Upcoming Deliveries ---- */}
      <Paper p="md">
        <Group justify="space-between" mb="md">
          <Group gap="sm">
            <Title order={4}>Upcoming Deliveries</Title>
            <IconTruck size={20} opacity={0.5} />
          </Group>
          <Button variant="subtle" size="xs" onClick={() => navigate('/purchasing/purchase-orders')}>
            View All
          </Button>
        </Group>
        <Table striped verticalSpacing="xs" horizontalSpacing="sm">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>PO</Table.Th>
              <Table.Th>Vendor</Table.Th>
              <Table.Th>Expected</Table.Th>
              <Table.Th ta="right">Items</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {safeKpis.upcoming_deliveries.map((d) => (
              <Table.Tr
                key={d.po_number}
                style={clickableRowStyle}
                className="hoverable-row"
                onClick={() => navigate('/purchasing/purchase-orders')}
              >
                <Table.Td>
                  <Text size="sm" fw={500}>
                    {d.po_number}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm" lineClamp={1}>
                    {d.vendor_name}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm">{d.expected_delivery}</Text>
                </Table.Td>
                <Table.Td ta="right">
                  <Text size="sm">{d.item_count}</Text>
                </Table.Td>
              </Table.Tr>
            ))}
            {safeKpis.upcoming_deliveries.length === 0 && (
              <Table.Tr>
                <Table.Td colSpan={4}>
                  <Text size="sm" c="dimmed" ta="center">
                    No upcoming deliveries
                  </Text>
                </Table.Td>
              </Table.Tr>
            )}
          </Table.Tbody>
        </Table>
      </Paper>

      {/* ---- Compliance Alerts ---- */}
      {safeKpis.compliance_alerts.length > 0 && (
        <Paper p="md">
          <Group justify="space-between" mb="md">
            <Title order={4}>Compliance Alerts</Title>
            <Badge color="orange" variant="light">
              {safeKpis.compliance_alerts.length}
            </Badge>
          </Group>
          <Stack gap="xs">
            {safeKpis.compliance_alerts.map((alert, i) => (
              <Alert
                key={i}
                color={
                  alert.severity === 'critical'
                    ? 'red'
                    : alert.severity === 'high'
                      ? 'orange'
                      : alert.severity === 'medium'
                        ? 'yellow'
                        : 'blue'
                }
                variant="light"
                icon={<IconAlertTriangle size={16} />}
                title={alert.type}
              >
                <Text size="sm">{alert.message}</Text>
                <Text size="xs" c="dimmed" mt={4}>
                  {alert.date}
                </Text>
              </Alert>
            ))}
          </Stack>
        </Paper>
      )}
    </Stack>
  );
}
